import { faCheck, faChevronDown, faTimes, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Combobox } from '@headlessui/react';
import _debounce from 'lodash/debounce';
import React, { type FC, useMemo, useRef, useState } from 'react';

import { OptionType } from '@/ui/components/Form/Select/types';

import { Props } from './types';

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

/**
 * @deprecated Do not use this component. AutocompleteMultiSelect, AutocompleteSingleSelect,
 * and Combobox have been condensed into a single Select component. Use the one at the root
 * of the UI folder instead (src/ui/Select, with the `multiple` prop).
 */
const AutocompleteMultiSelect: FC<Props> = ({
  debounce = true,
  disabled = false,
  error,
  helpText,
  label,
  missingOptionsText = 'No options',
  onChange,
  onSearch,
  options,
  placeholder = '',
  selectedOptions = [],
  showLabel = true
}: Props) => {
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedValues = useMemo(
    () => new Set(selectedOptions.map((o) => o.value)),
    [selectedOptions]
  );

  const filteredOptions = useMemo(
    () =>
      options
        .filter((option) => !selectedValues.has(option.value))
        .filter(
          (option) => query === '' || option.label.toLowerCase().includes(query.toLowerCase())
        ),
    [options, query, selectedValues]
  );

  const debounceSearch = useMemo(
    () => _debounce((value: string) => onSearch?.(value), 500, { leading: false, trailing: true }),
    [onSearch]
  );

  const handleSearch = (value: string) => {
    setQuery(value);
    if (debounce) {
      debounceSearch(value);
    } else {
      onSearch?.(value);
    }
  };

  const handleChange = (options: OptionType<string>[]) => {
    setQuery('');
    onChange(options);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const removeOption = (value: string) => {
    const next = selectedOptions.filter((o) => o.value !== value);
    onChange(next);
  };

  const disabledClass = disabled ? 'opacity-50 cursor-not-allowed' : '';

  return (
    <Combobox
      disabled={disabled}
      immediate
      multiple
      onChange={handleChange}
      value={selectedOptions}>
      <Combobox.Label
        className={classNames(
          'block font-medium leading-6 text-gray-700',
          showLabel ? '' : 'hidden'
        )}>
        {label}
      </Combobox.Label>

      {helpText && <p className="mt-1 text-sm text-gray-600">{helpText}</p>}

      <div
        className={classNames(
          'relative mt-1 flex flex-wrap items-center gap-1 rounded-md bg-white px-2 py-1.5 shadow-xs ring-1 ring-inset',
          error ? 'ring-red-400' : 'ring-gray-300',
          'focus-within:ring-2 focus-within:ring-indigo-600',
          disabledClass
        )}
        onClick={() => !disabled && inputRef.current?.focus()}>
        {selectedOptions.map((option) => (
          <span
            key={option.value}
            className="inline-flex items-center gap-1 rounded bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-800"
            data-testid={`token-${option.value}`}>
            {option.label}
            {!disabled && (
              <button
                aria-label={`Remove ${option.label}`}
                className="ml-0.5 text-indigo-500 hover:text-indigo-700 focus:outline-none"
                onClick={(e) => {
                  e.stopPropagation();
                  removeOption(option.value);
                }}
                type="button">
                <FontAwesomeIcon icon={faTimesCircle} className="h-3 w-3" aria-hidden="true" />
              </button>
            )}
          </span>
        ))}

        <Combobox.Button className="flex min-w-0 flex-1 items-center focus:outline-none">
          <Combobox.Input
            ref={inputRef}
            className={classNames(
              'min-w-[120px] flex-1 border-0 bg-transparent p-0 text-sm text-gray-900 placeholder-gray-400 outline-none focus:ring-0',
              error ? 'placeholder-red-400' : '',
              disabledClass
            )}
            displayValue={() => query}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder={selectedOptions.length === 0 ? placeholder : ''}
          />
          <span className="pointer-events-none flex items-center pr-1">
            <FontAwesomeIcon
              icon={faChevronDown}
              className="h-3 w-3 text-gray-400"
              aria-hidden="true"
            />
          </span>
        </Combobox.Button>
        <Combobox.Options className="absolute left-0 top-full z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
          {filteredOptions.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-400">{missingOptionsText}</div>
          ) : (
            filteredOptions.map((option) => (
              <Combobox.Option
                key={option.value}
                value={option}
                className={({ active }) =>
                  classNames(
                    active ? 'bg-indigo-600 text-white' : '',
                    'relative cursor-default select-none py-2 pl-8 pr-4'
                  )
                }>
                {({ active, selected }) => (
                  <>
                    <span className={classNames('block truncate', selected ? 'font-semibold' : '')}>
                      {option.label}
                    </span>
                    {selected && (
                      <span
                        className={classNames(
                          'absolute inset-y-0 left-0 flex items-center pl-1.5',
                          active ? 'text-white' : 'text-indigo-600'
                        )}>
                        <FontAwesomeIcon
                          icon={faCheck}
                          className="h-3.5 w-3.5"
                          aria-hidden="true"
                        />
                      </span>
                    )}
                  </>
                )}
              </Combobox.Option>
            ))
          )}
        </Combobox.Options>
      </div>

      {error && (
        <div className="mt-1 flex items-center gap-1 text-sm text-red-500">
          <FontAwesomeIcon icon={faTimes} className="h-3.5 w-3.5" aria-hidden="true" />
          {error}
        </div>
      )}
    </Combobox>
  );
};

export default React.memo(AutocompleteMultiSelect);
